/**
 * Cameras Dashboard - Main camera management interface
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,

} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Videocam as VideocamIcon,
  Error as ErrorIcon,
  CheckCircle as OnlineIcon,
  Warning as OfflineIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useSiteContext } from '../../hooks/useSiteContext';
import { CameraViewer } from '../../components/Cameras/CameraViewer';
import { IPCamera, cameraService } from '../../services/cameraService';

export const CamerasDashboard: React.FC = () => {
  const { currentSite } = useSiteContext();
  const [cameras, setCameras] = useState<IPCamera[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  

  
  useEffect(() => {
    if (currentSite) {
      fetchCameras();
    }
  }, [currentSite]);
  
  const fetchCameras = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!currentSite) return;
      
      const response = await cameraService.getCameras(currentSite.siteId);
      setCameras(response.results);
      
    } catch (err) {
      console.error('Failed to fetch cameras:', err);
      setError('Failed to load cameras');
    } finally {
      setLoading(false);
    }
  };
  

  
  const handleRefresh = () => {
    fetchCameras();
  };
  
  const filteredCameras = cameras.filter(camera => {
    const matchesSearch = camera.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         camera.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || camera.connection_status === statusFilter;
    const matchesType = typeFilter === 'all' || camera.camera_type === typeFilter;
    const matchesLocation = locationFilter === 'all' || 
                           camera.location_name === locationFilter ||
                           camera.plant_area_name === locationFilter;
    
    return matchesSearch && matchesStatus && matchesType && matchesLocation;
  });

  // Get unique locations for filter dropdown
  const uniqueLocations = Array.from(new Set(
    cameras.map(camera => camera.location_name || camera.plant_area_name)
      .filter(location => location !== null && location !== '')
  )).sort();
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <OnlineIcon sx={{ color: 'success.main' }} />;
      case 'offline':
        return <OfflineIcon sx={{ color: 'warning.main' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      default:
        return <OfflineIcon sx={{ color: 'text.disabled' }} />;
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };
  
  if (!currentSite) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Please select a site to view cameras.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            📹 Camera Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Live video monitoring and security surveillance for {currentSite.siteName}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>
      

      
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search cameras..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                  <MenuItem value="offline">Offline</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="fixed">Fixed Camera</MenuItem>
                  <MenuItem value="ptz">PTZ Camera</MenuItem>
                  <MenuItem value="dome">Dome Camera</MenuItem>
                  <MenuItem value="bullet">Bullet Camera</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={locationFilter}
                  label="Location"
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <MenuItem value="all">All Locations</MenuItem>
                  {uniqueLocations.map((location) => (
                    <MenuItem key={location} value={location}>
                      {location}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="text.secondary">
                {filteredCameras.length} of {cameras.length} cameras
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Camera Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button onClick={handleRefresh} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      ) : filteredCameras.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <VideocamIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {cameras.length === 0 ? 'No cameras configured' : 'No cameras match your filters'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {cameras.length === 0 
                ? 'Add your first camera to start monitoring'
                : 'Try adjusting your search or filter criteria'
              }
            </Typography>
            {cameras.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Contact your administrator to add cameras to this site.
              </Typography>
            )}
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {filteredCameras.map((camera, index) => (
            <Grid item xs={12} md={6} lg={4} key={camera.id}>
              <CameraViewer
                camera={camera}
                autoRefreshInterval={camera.update_interval}
                onError={(error) => console.error('Camera error:', error)}
                // Stagger initial loading to prevent overwhelming the camera service
                  initialLoadDelay={index * 100} // Reduced from 300ms to 100ms for faster loading // 0.3s delay between each camera
              />
            </Grid>
          ))}
        </Grid>
      )}
      

    </Box>
  );
}; 